<template>
  <div class="relative" @click.stop>
    <div ref="triggerEl" class="actions-menu-wrapper">
      <slot name="trigger">
        <button
          type="button"
          class="focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-2.5 py-0 text-center dark:bg-gray-100 dark:hover:bg-gray-700 dark:focus:ring-gray-800"
        >
          <EllipsisIcon
            class="tooltip-trigger"
            data-cy="tooltip-trigger"
            width="20"
          />
        </button>
      </slot>
    </div>
    <div
      id="actions-menu-click"
      ref="targetEl"
      data-popover
      role="tooltip"
      class="z-20 inline-block w-64 text-sm text-gray-500 transition-opacity duration-300 bg-white border border-gray-200 rounded-lg shadow-sm dark:text-gray-400 dark:border-gray-600 dark:bg-gray-800"
    >
      <div class="px-3 py-2">
        <ul class="actions-menu-list text-sm !text-gray-700 dark:text-gray-200">
          <!-- The eslint rule below is for Vue 2 (`<template v-for>` could
               not be keyed); on Vue 3.5 the key MUST sit on the
               template tag when its body has multiple children. -->
          <!-- eslint-disable-next-line vue/no-v-for-template-key -->
          <template v-for="(action, idx) in visibleActions" :key="action.label">
            <li
              v-if="showDividerBefore(idx)"
              class="actions-menu-divider"
              aria-hidden="true"
              data-cy="actions-menu-divider"
            />
            <li
              data-cy="actions-menu-action"
              :title="action.disabled ? action.title : undefined"
              :class="[
                `!text-gray-700 text-left px-2 flex items-center rounded-lg font-medium py-2 no-underline hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white action-item ${
                  action.className || ''
                }`,
                { 'text-gray-300': action.disabled }
              ]"
              @click="performAction(action)"
            >
              <component
                :is="action.source"
                v-if="action.source"
                class="mr-2 image-icon"
              ></component>
              <i v-if="action.icon" :class="[action.icon, 'mr-2']"></i>
              <span v-if="action.label">{{ action.label }}</span>
              <!-- action.html comes from the actions array config (not
                   user input), so v-html is safe here. -->
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span v-if="action.html" v-html="action.html"></span>
            </li>
          </template>
        </ul>
      </div>
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Instance, Props } from "tippy.js";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

import EllipsisIcon from "@/images/dots-horizontal-outline.svg?component";
import "../../../vendor/assets/javascripts/tippy/themes/light.css";

const emit = defineEmits<{
  (e: string): void;
  (e: "on-show", instance: Instance): void;
  (e: "on-hide", instance: Instance): void;
}>();

interface Action {
  label: string;
  icon?: string;
  eventName: string;
  className?: string;
  visible?: boolean;
  disabled?: boolean;
  title?: string;
  source?: string;
  html?: string;
  group?: string;
}

const props = defineProps<{
  item?: any;
  actions: Action[];
  options?: Props;
  placement?: "top" | "bottom" | "left" | "right";
}>();

const targetEl = ref(null);
const triggerEl = ref(null);
const popover = ref<Instance | null>(null);

const onShow = (instance: Instance) => emit("on-show", instance);
const onHide = (instance: Instance) => emit("on-hide", instance);

const defaultOptions: Partial<Props> = {
  appendTo: document.body,
  interactive: true,
  delay: [0, 0],
  hideOnClick: true,
  arrow: false,
  placement: "left",
  trigger: "click",
  theme: "actions-menu light",
  onShow,
  onHide
};

const visibleActions = computed(() => {
  return props.actions.filter(
    (action) => !Object.keys(action).includes("visible") || action.visible
  );
});

// Insert a divider before an item only when its `group` differs from the
// previous visible item's group AND both groups are set. Adjacent items in
// the same group, or items with no group at all, don't get a separator.
function showDividerBefore(index: number): boolean {
  if (index === 0) return false;
  const current = visibleActions.value[index]?.group;
  const previous = visibleActions.value[index - 1]?.group;
  if (!current || !previous) return false;
  return current !== previous;
}

// When ActionsMenu is rendered inside a datatable "actions" cell
// (className `th-no-text`), expand the click target to the entire cell
// so users with imperfect aim — and touch-device users in particular —
// don't have to hit the small ellipsis icon precisely. Stretching the
// wrapper via CSS works in Chromium but is unreliable in Firefox when
// the cell is `position: sticky`, so this JS fallback attaches a click
// handler directly to the host cell and dispatches the open to tippy.
let cellClickHandler: ((e: MouseEvent) => void) | null = null;
let hostCell: HTMLElement | null = null;
function attachCellClickFallback() {
  if (!triggerEl.value) return;
  const cell = (triggerEl.value as HTMLElement).closest(
    "td.th-no-text"
  ) as HTMLElement | null;
  if (!cell) return;
  hostCell = cell;
  cellClickHandler = (event: MouseEvent) => {
    const trigger = triggerEl.value as HTMLElement | null;
    if (!trigger) return;
    // Tippy already wires its own click listener to the trigger; if the
    // click is already inside it, let tippy handle it untouched.
    if (trigger.contains(event.target as Node)) return;
    // The Datatable's delegated row-click handler also runs on this
    // bubble path — stop it so the row doesn't navigate.
    event.stopPropagation();
    event.preventDefault();
    // Forward the click to the trigger so tippy's own machinery
    // toggles the menu (correctly handling delay, hideOnClick, etc.).
    // Calling .click() preserves the existing show/hide cadence,
    // unlike popover.show() which fights tippy's click-toggle state.
    trigger.click();
  };
  cell.addEventListener("click", cellClickHandler);
}

onBeforeUnmount(() => {
  if (hostCell && cellClickHandler) {
    hostCell.removeEventListener("click", cellClickHandler);
  }
});

onMounted(() => {
  // @ts-expect-error Missing global tippy ts declaration
  popover.value = tippy(triggerEl.value, {
    ...defaultOptions,
    content: targetEl.value,
    ...props.options
  } as Props);
  attachCellClickFallback();
});

const performAction = (action: Action) => {
  if (!action.disabled) {
    if (action.eventName) {
      popover.value?.hide();
      emit(action.eventName);
    }
  }
};
</script>

<style lang="scss" scoped>
ul {
  min-width: 100px;
  list-style-type: none;
  margin: 0;
  padding: 0;
  li {
    white-space: normal;
    cursor: pointer;
    &.disabled {
      cursor: not-allowed !important;
    }
    &.actions-menu-divider {
      cursor: default;
      height: 1px;
      margin: 4px 8px;
      padding: 0;
      background: #e5e7eb;
      border-radius: 0;
    }
    &.actions-menu-divider:hover {
      background: #e5e7eb;
    }
    &.action-link-wrapper {
      position: relative;
      min-height: 36px;

      :deep(a.action-link) {
        display: flex;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 8px 8px 8px 32px;
        color: inherit;
        text-decoration: none;
      }
    }
  }
}
.image-icon {
  width: 16px;
  height: 16px;
}

.tooltip-trigger {
  text-align: center;
  cursor: pointer;
}
</style>
